import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { getCountryLocation } from '@rbilabs/intl';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { ILocation, Nullable } from '@rbi-ctg/frontend';
import { GeolocationDeniedModal } from 'components/geolocation-permission-modal/geolocation-denied-modal';
import { GoogleGeolocationLibraryLoader } from 'components/google-geolocation-library-loader';
import { useSendUpdateUserAttributesEventMutation } from 'generated/graphql-gateway';
import { useEffectOnUnmount } from 'hooks/use-effect-on-unmount';
import { useFeatureExternalLink } from 'hooks/use-feature-external-link';
import { useMediaQuery } from 'hooks/use-media-query';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useServiceModeContext } from 'state/service-mode';
import { getCountry } from 'utils/environment';
import { isValidPosition } from 'utils/geolocation';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';
import { ServiceModeCategory } from 'utils/service-mode';

import { useSearchRestaurants } from '../hooks/use-search-restaurants';
import { useStoreLocatorView } from '../hooks/use-store-locator-view';
import { DeliveryRoutes } from '../routes';
import { StoreLocatorSteps, StoreLocatorViews } from '../types';

import { useServiceModeCategory } from './hooks/use-service-mode-category';
import { useStoresReducer } from './hooks/use-stores-reducer';
import { StoreLocatorRoutes } from './store-locator-routes';
import { StoreLocatorView } from './store-locator.view';

/**
 *
 * StoreLocator purpose
 *
 */

const DEFAULT_SHOW_STORE_LOCATOR = true;

const StoreLocatorHome: FC = () => {
  const { formatMessage } = useIntl();

  const isDesktop = useMediaQuery('desktop');

  const enableDeliveryOnly = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);
  const enableListRestaurantsInParallel = useFlag(
    LaunchDarklyFlag.ENABLE_LIST_RESTAURANTS_IN_PARALLEL
  );
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableDeliveryOnSameScreen = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_ADDRESS_ON_SAME_SCREEN
  );
  const enableAutomaticLocationShareAndKbClose = useFlag(
    LaunchDarklyFlag.ENABLE_AUTOMATIC_LOCATION_SHARE_AND_KEYBOARD_CLOSE
  );

  const showStoreLocator =
    useFlag(LaunchDarklyFlag.SHOW_STORE_LOCATOR) ?? DEFAULT_SHOW_STORE_LOCATOR;
  const { storeLocatorCallbackUrl, setStoreLocatorCallbackUrl } = useLocationContext();
  const redirectUrl = storeLocatorCallbackUrl || routes.menu;
  const location = useLocation();
  const { featureExternalLink, featureExternalLinkLoading } = useFeatureExternalLink();
  const isMobile = useMediaQuery('headerMobile');
  const performanceLog = useRef<{
    activeCoordinates?: Nullable<ILocation>;
    start?: number;
    end?: number;
  }>({});
  const STORE_LOCATOR_LOG_PREFIX = '[store-locator-view]';

  // cleaning up the callback url once the user gets away from the store locator page
  useEffectOnUnmount(() => {
    setStoreLocatorCallbackUrl('');
  });

  const [externalLinkDelivery, setExternalLinkDelivery] = useState('');

  const {
    errorFavs,
    errorNearby,
    errorRecent,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    storesNearby,
    storesFavs,
    storesRecent,
    storeLocatorDispatch,
  } = useStoresReducer();
  const { serviceMode, setServiceMode } = useServiceModeContext();

  const {
    isPermissionPrompt,
    activeCoordinates,
    manualAddress,
    isPermissionGranted,
    resetCoordsAndLoadCurrentPosition,
    setCoordinatesManual,
    setManualAddress,
    updateManualCoordinates,
    promptForLocation,
  } = useGeolocation();

  const [geolocationDeniedModaOpen, setGeolocationDeniedModaOpen] = useState(
    !isPermissionPrompt && !isPermissionGranted
  );

  const {
    ErrorDialog,
    Dialog,
    searchFavRestaurants,
    searchNearbyRestaurants,
    searchRecentRestaurants,
  } = useSearchRestaurants({
    storeLocatorDispatch,
  });

  const {
    currentView,
    currentStep,
    updateStoreLocatorView,
    setCurrentStep,
    editedAddress,
    setEditedAddress,
  } = useStoreLocatorView();

  const { serviceModeCategory, setServiceModeCategory, serviceModeCategoryOptions } =
    useServiceModeCategory();

  const navigate = useNavigate();
  const { trackEvent, updateUserAttributes } = useCdpContext();
  const [sendUpdateUserAttributesEvent] = useSendUpdateUserAttributesEventMutation();
  const [prevCoordinates, setPrevCoordinates] = useState<Nullable<ILocation>>(activeCoordinates);
  const onServiceModeCategoryChange = useCallback(
    (value: ServiceModeCategory) => {
      setServiceModeCategory(value);
      setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);

      trackEvent({
        name: CustomEventNames.BUTTON_CLICK,
        type: EventTypes.Other,
        attributes: {
          Name: CustomEventNames.BUTTON_CLICK_SERVICE_MODE_TOGGLE,
          'Service Mode': value,
        },
      });
      updateUserAttributes({ 'Service Mode': value }, {}, sendUpdateUserAttributesEvent);

      switch (value) {
        case ServiceModeCategory.PICKUP:
          navigate(routes.storeLocator);
          setServiceMode(null);
          break;
        case ServiceModeCategory.DELIVERY:
          if (externalLinkDelivery) {
            window.open(externalLinkDelivery, '_blank', 'noopener');
            setServiceModeCategory(ServiceModeCategory.PICKUP);
          } else {
            updateStoreLocatorView(StoreLocatorViews.LIST_VIEW);
            navigate(routes.address);
          }
          break;
        default:
          break;
      }
    },
    [
      externalLinkDelivery,
      trackEvent,
      navigate,
      setCurrentStep,
      setServiceMode,
      setServiceModeCategory,
      updateStoreLocatorView,
    ]
  );

  const onSearchClick = useCallback(
    (newCoords: ILocation) => {
      setCoordinatesManual({ newCoords });
      setManualAddress('');
    },
    [setCoordinatesManual, setManualAddress]
  );

  const onConfirmBackClick = useCallback(() => {
    setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);
    navigate(routes.address);
  }, [navigate, setCurrentStep]);

  /**
   * Used to mesure the time it takes to load the nearby restaurants
   **/
  useEffect(() => {
    if (!isValidPosition(activeCoordinates) || searchingNearby) {
      return;
    }

    const changedCoordinates = performanceLog.current?.activeCoordinates !== activeCoordinates;

    if (changedCoordinates) {
      performanceLog.current = {
        activeCoordinates,
        start: performance.now(),
      };
    } else {
      if (performanceLog.current?.activeCoordinates && !performanceLog.current?.end) {
        performanceLog.current = { ...performanceLog.current, end: performance.now() };

        const loadingTime =
          (performanceLog.current?.end ?? 0) - (performanceLog.current?.start ?? 0);

        logger.info(
          `${STORE_LOCATOR_LOG_PREFIX} Time to load the restaurant list: ${loadingTime} milliseconds`,
          {
            ...performanceLog.current,
            loadingTime,
            enableListRestaurantsInParallel: (!!enableListRestaurantsInParallel).toString(),
          }
        );
      }
    }
  }, [
    activeCoordinates,
    serviceMode,
    storesNearby,
    searchingNearby,
    enableListRestaurantsInParallel,
  ]);

  useEffect(() => {
    if (isValidPosition(activeCoordinates)) {
      searchNearbyRestaurants({
        location: activeCoordinates,
      });
    }
    // Triggers a search when the service mode change to keep the list in sync
  }, [activeCoordinates, searchNearbyRestaurants, serviceMode]);

  useEffect(() => {
    searchFavRestaurants(activeCoordinates);
  }, [activeCoordinates, searchFavRestaurants]);

  useEffect(() => {
    searchRecentRestaurants(activeCoordinates);
  }, [activeCoordinates, searchRecentRestaurants]);

  useEffect(() => {
    if (!featureExternalLinkLoading) {
      const deliveryExternalLink = featureExternalLink?.FeatureExternalLinks?.find(
        item => item?.title === 'Delivery'
      );
      const deliveryLink = deliveryExternalLink?.link?.localeUrl ?? '';
      setExternalLinkDelivery(deliveryLink);
    }
  }, [featureExternalLink, featureExternalLinkLoading]);

  useEffect(() => {
    if (enableAutomaticLocationShareAndKbClose && isPermissionPrompt) {
      promptForLocation();
    }
  }, [enableAutomaticLocationShareAndKbClose, isPermissionPrompt, promptForLocation]);

  const onSearchBarValueChange = useCallback(
    (address: string | undefined | null) => {
      setManualAddress(address ?? '');
    },
    [setManualAddress]
  );

  const dismissAddressModalUrl = routes.storeLocator;

  if (enableDeliveryOnly) {
    return (
      <DeliveryRoutes
        deliveryModalPath="*"
        dismissAddressModalUrl={routes.storeLocator}
        redirectUrl={redirectUrl}
      />
    );
  }

  const shouldHideCategories =
    [StoreLocatorSteps.CONFIRM_ADDRESS, StoreLocatorSteps.SAVED_ADDRESS].includes(currentStep) &&
    isMobile;

  const coordinatesDiffer =
    prevCoordinates &&
    activeCoordinates &&
    (prevCoordinates.lat !== activeCoordinates.lat ||
      prevCoordinates.lng !== activeCoordinates.lng);

  if (!showStoreLocator && !isPermissionGranted && (!prevCoordinates || coordinatesDiffer)) {
    const newCoords = getCountryLocation(getCountry());
    if (newCoords) {
      setCoordinatesManual({ newCoords });
      setPrevCoordinates(newCoords);
    }
  }

  return (
    <>
      <Helmet title={formatMessage({ id: 'restaurants' })} />
      <StoreLocatorView
        activeCoordinates={activeCoordinates}
        errorFavs={errorFavs}
        errorNearby={errorNearby}
        errorRecent={errorRecent}
        handleResetLocation={resetCoordsAndLoadCurrentPosition}
        searchingNearby={searchingNearby}
        searchingRecent={searchingRecent}
        searchingFavs={searchingFavs}
        searchNearbyRestaurants={searchNearbyRestaurants}
        storesFavs={storesFavs}
        storesNearby={storesNearby}
        storesRecent={storesRecent}
        // Map View Props
        onSearchClick={onSearchClick}
        // View Props
        currentView={currentView}
        onViewChange={updateStoreLocatorView}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        onConfirmBackClick={onConfirmBackClick}
        // Search Bar Props
        onAddressSelected={updateManualCoordinates}
        searchBarValue={manualAddress}
        onSearchBarValueChange={onSearchBarValueChange}
        shouldFocusSearchBar={!manualAddress && isPermissionGranted && isDesktop}
        // Service Mode Category Props
        hideServiceModeCategories={!enableOrdering || shouldHideCategories}
        selectedServiceModeCategory={serviceModeCategory}
        serviceModeCategoryOptions={serviceModeCategoryOptions}
        onServiceModeCategoryChange={onServiceModeCategoryChange}
        isDeliveryModeSelected={enableDeliveryOnSameScreen && location.pathname === routes.address}
        deliveryOnSameScreen={enableDeliveryOnSameScreen}
        showStoreLocator={showStoreLocator}
        // Edit Address props
        editedAddress={editedAddress}
        setEditedAddress={setEditedAddress}
      />

      <StoreLocatorRoutes
        dismissAddressModalUrl={dismissAddressModalUrl}
        redirectUrl={redirectUrl}
        deliveryNoModal={enableDeliveryOnSameScreen}
      />

      <ErrorDialog />
      <Dialog />

      {enableAutomaticLocationShareAndKbClose && (
        <GeolocationDeniedModal
          isOpen={geolocationDeniedModaOpen}
          dismissModal={() => setGeolocationDeniedModaOpen(false)}
        />
      )}
    </>
  );
};

export const StoreLocator = () => (
  <GoogleGeolocationLibraryLoader>
    <StoreLocatorHome />
  </GoogleGeolocationLibraryLoader>
);
